import React from 'react';
import { styled, TableRow, TableCell } from '@mui/material';
import { Purchase } from 'src/dto/Credits';
import { currencyFixedDigitsFormat } from 'src/utils/number';
import { useGetReceiptQuery } from 'src/redux/services/receipt';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';

import EmptyState from 'src/frontend-common/components/EmptyState';
import SnackBarCustom from 'src/frontend-common/components/SnackbarCustom';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

export interface BillingReceiptListProps {
  list: Purchase[];
}

const ReceiptLink = styled('button')(({ theme }) => ({
  color: theme.palette.primary.main,
  textTransform: 'capitalize',
  fontSize: 14,
  fontWeight: 500,
  backgroundColor: 'white',
  boxShadow: 'none',
  border: 'none',
  textDecoration: 'underline',
  '&:hover': {
    cursor: 'pointer',
  },
}));

const LoadingScreenBgWhite = styled(LoadingScreen)(() => ({
  backgroundColor: 'white',
  alignItems: 'flex-end',
}));

const ReceiptsList: React.FC<BillingReceiptListProps> = ({ list: listOfReceipts }) => {
  const [receiptId, setReceiptId] = React.useState<number | null>(null);
  const [lastDownload, setLastDownload] = React.useState<number | null>(null);
  const [showSnackBar, setShowSnackBar] = React.useState<boolean>(false);

  const {
    data,
    isFetching,
    isError: receiptCallError,
  } = useGetReceiptQuery(receiptId, {
    skip: !receiptId,
  });

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  if (receiptCallError) {
    setShowSnackBar(true);
  }

  if (!isFetching && !receiptCallError && data) {
    if (receiptId !== lastDownload) {
      const link = document.createElement('a');
      link.href = data.file_url;
      link.target = '_blank';
      link.rel = 'norefferer';
      link.click();
      setLastDownload(receiptId);
    }
  }

  return (
    <>
      {listOfReceipts &&
        listOfReceipts.length > 0 &&
        listOfReceipts.map((receipt) => {
          const date = new Date(receipt.activation_date);
          return (
            <React.Fragment key={`${receipt.value}${Math.random()}`}>
              <TableRow>
                <TableCell>{formatDate(date)}</TableCell>
                <TableCell>
                  {currencyFixedDigitsFormat(receipt.value, 2, {
                    value: useGetCurrency(),
                    currencyPosition: 'left',
                  })}
                </TableCell>
                <TableCell>
                  {isFetching && receiptId === receipt.id && <LoadingScreenBgWhite variant="circle" width={25} />}
                  {/* if is not fetching or it is fetching but the receipt id is not the same as the one we are trying to fetch */}
                  {(!isFetching || (isFetching && receiptId !== receipt.id)) && (
                    <ReceiptLink onClick={() => setReceiptId(receipt.id)}>View receipt</ReceiptLink>
                  )}
                </TableCell>
              </TableRow>
              <SnackBarCustom open={showSnackBar} onClose={() => setShowSnackBar(false)} message="Error while fetching the receipt" severity="error" />
            </React.Fragment>
          );
        })}
      {listOfReceipts && listOfReceipts.length === 0 && (
        <TableRow>
          <TableCell colSpan={3}>
            <EmptyState width={80} marginBottom={8} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ReceiptsList;
